
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Volume, BoxVolume} from "./Volume.js";
import {Utils} from "../utils.js";
import { EventDispatcher } from "../EventDispatcher.js";
/**
 * Tool for managing, creating, and editing volumes in a point cloud viewer.
 * Inherits from EventDispatcher to handle custom events related to volume operations.
 * @class
 */
export class VolumeTool extends EventDispatcher{
	/**
     * Creates an instance of VolumeTool.
	 * @constructor
     * @param {Potree.Viewer} viewer - The Potree viewer instance.
     */
	constructor (viewer) {
		super();
		/**
         * The Potree viewer instance.
         * @type {Potree.Viewer}
         */
		this.viewer = viewer;
		/**
         * Renderer associated with the viewer.
         * @type {THREE.WebGLRenderer}
         */
		this.renderer = viewer.renderer;

		this.addEventListener('start_inserting_volume', e => {
			this.viewer.dispatchEvent({
				type: 'cancel_insertions'
			});
		});
		/**
         * A THREE.Scene instance to manage the volume tool's own scene.
         * @type {THREE.Scene}
         */
		this.scene = new THREE.Scene();
		this.scene.name = 'scene_volume';

		this.viewer.inputHandler.registerInteractiveScene(this.scene);

		this.onRemove = e => {
			this.scene.remove(e.volume);
		};

		this.onAdd = e => {
			this.scene.add(e.volume);
		};

		for(let volume of viewer.scene.volumes){
			this.onAdd({volume: volume});
		}

		this.viewer.inputHandler.addEventListener('delete', e => {
			let volumes = e.selection.filter(e => (e instanceof Volume));
			volumes.forEach(e => this.viewer.scene.removeVolume(e));
		});

		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.scene", e => this.render(e));
		viewer.addEventListener("scene_changed", this.onSceneChange.bind(this));

		viewer.scene.addEventListener('volume_added', this.onAdd);
		viewer.scene.addEventListener('volume_removed', this.onRemove);
	}
	/**
     * Handles changes to the scene, such as adding or removing volumes.
     * @param {Object} e - The event object containing scene information.
     */
	onSceneChange(e){
		if(e.oldScene){
			e.oldScene.removeEventListeners('volume_added', this.onAdd);
			e.oldScene.removeEventListeners('volume_removed', this.onRemove);
		}

		e.scene.addEventListener('volume_added', this.onAdd);
		e.scene.addEventListener('volume_removed', this.onRemove);
	}
	/**
     * Starts the process of inserting a new volume into the scene.
     * @param {Object} args - Configuration arguments for the new volume.
     * @returns {Volume} - The newly created volume.
     */
	startInsertion (args = {}) {
		let volume;
		if(args.type){
			volume = new args.type();
		}else{
			volume = new BoxVolume();
		}
		
		volume.clip = args.clip || false;
		volume.name = args.name || 'Volume';

		this.dispatchEvent({
			type: 'start_inserting_volume',
			volume: volume
		});

		this.viewer.scene.addVolume(volume);
		this.scene.add(volume);

		let cancel = {
			callback: null
		};

		let drag = e => {
			let camera = this.viewer.scene.getActiveCamera();
			
			let I = Utils.getMousePointCloudIntersection(
				e.drag.end, 
				this.viewer.scene.getActiveCamera(), 
				this.viewer, 
				this.viewer.scene.pointclouds, 
				{pickClipped: false});

			if (I) {
				volume.position.copy(I.location);

				let wp = volume.getWorldPosition(new THREE.Vector3()).applyMatrix4(camera.matrixWorldInverse);
				// let pp = new THREE.Vector4(wp.x, wp.y, wp.z).applyMatrix4(camera.projectionMatrix);
				let w = Math.abs((wp.z / 5));
				volume.scale.set(w, w, w);
			}
		};

		let drop = e => {
			volume.removeEventListener('drag', drag);
			volume.removeEventListener('drop', drop);

			cancel.callback();
		};

		cancel.callback = e => {
			volume.removeEventListener('drag', drag);
			volume.removeEventListener('drop', drop);
			this.viewer.removeEventListener('cancel_insertions', cancel.callback);
		};

		volume.addEventListener('drag', drag);
		volume.addEventListener('drop', drop);
		this.viewer.addEventListener('cancel_insertions', cancel.callback);

		this.viewer.inputHandler.startDragging(volume);

		return volume;
	}

	/**
     * Similar to startInsertion, but initializes the volume based on the model's bounding box.
     * @param {Object} args - Configuration arguments for the new volume.
     * @returns {Volume} - The newly created volume.
     */
	startInsertion2 (args = {}) {
		let volume;
		if(args.type){
			volume = new args.type();
		}else{
			volume = new BoxVolume();
		}

		// select refer pointclouds boundingbox
		let pcList = [];
		if (args.pcIds) {
			for (let i = 0; i < this.viewer.scene.pointclouds.length; i++) {
				const element = this.viewer.scene.pointclouds[i];
				if (args.pcIds.indexOf(element.uuid) > -1) {
					pcList = [...pcList, element];
				}
			}
		} else {
			pcList = this.viewer.scene.pointclouds;
		}

		volume.clip = args.clip || false;
		volume.name = args.name || 'Volume';

		this.dispatchEvent({
			type: 'start_inserting_volume',
			volume: volume
		});

		this.viewer.scene.addVolume(volume);
		this.scene.add(volume);

		// get boundingBox, set center and size
		let boundingBox = this.viewer.scene.getBoundingBox(pcList);
		let center = boundingBox.getCenter(new THREE.Vector3());
		volume.position.set(center.x, center.y, center.z);
		let boxSize = boundingBox.getSize(new THREE.Vector3());
		let x = Math.abs(boxSize.x);
		let y = Math.abs(boxSize.y);
		let z = Math.abs(boxSize.z);
		volume.scale.set(x, y, z);

		return volume;
	}
	/**
     * Updates the tool, typically called on each animation frame.
     */
	update(){
		if (!this.viewer.scene) {
			return;
		}
		
		let camera = this.viewer.scene.getActiveCamera();
		let renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		let clientWidth = renderAreaSize.width;
		let clientHeight = renderAreaSize.height;

		let volumes = this.viewer.scene.volumes;
		for (let volume of volumes) {
			let label = volume.label;
			
			{

				let distance = label.position.distanceTo(camera.position);
				let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

				let scale = (70 / pr);
				label.scale.set(scale, scale, scale);
			}

			let calculatedVolume = volume.getVolume();
			calculatedVolume = calculatedVolume / Math.pow(this.viewer.lengthUnit.unitspermeter, 3) * Math.pow(this.viewer.lengthUnitDisplay.unitspermeter, 3);  //convert to cubic meters then to the cubic display unit
			let text = Utils.addCommas(calculatedVolume.toFixed(3)) + ' ' + this.viewer.lengthUnitDisplay.code + '\u00B3';
			label.setText(text);
		}
	}
	/**
     * Renders the volume tool's scene.
     * @param {Object} params - Rendering parameters.
     */
	render(params){
		const renderer = this.viewer.renderer;

		const oldTarget = renderer.getRenderTarget();
		
		if(params.renderTarget){
			renderer.setRenderTarget(params.renderTarget);
		}
		renderer.render(this.scene, this.viewer.scene.getActiveCamera());
		renderer.setRenderTarget(oldTarget);
	}

}
